import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import React, { useState } from "react";

interface CreateDatasetDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (name: string, description: string) => Promise<void>;
  loading: boolean;
}

export const CreateDatasetDialog: React.FC<CreateDatasetDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");

  const handleClose = () => {
    if (!loading) {
      setName("");
      setDescription("");
      setNameError("");
      setDescriptionError("");
      onClose();
    }
  };

  const validateForm = () => {
    let isValid = true;

    setNameError("");
    setDescriptionError("");

    if (!name.trim()) {
      setNameError("Dataset name is required");
      isValid = false;
    } else if (name.trim().length < 3) {
      setNameError("Dataset name must be at least 3 characters");
      isValid = false;
    }

    if (!description.trim()) {
      setDescriptionError("Dataset description is required");
      isValid = false;
    } else if (description.trim().length < 10) {
      setDescriptionError("Dataset description must be at least 10 characters");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      await onConfirm(name.trim(), description.trim());
      handleClose();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Dataset</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            label="Dataset Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyPress={handleKeyPress}
            error={!!nameError}
            helperText={nameError}
            margin="normal"
            disabled={loading}
            autoFocus
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!descriptionError}
            helperText={descriptionError}
            margin="normal"
            disabled={loading}
            placeholder="Describe the purpose and contents of this dataset..."
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This dataset will be used to store and manage test data for conversation lens calibration.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !name.trim() || !description.trim()}
        >
          Create Dataset
        </Button>
      </DialogActions>
    </Dialog>
  );
};
